import { LoginRequest, SignupRequest, JwtResponse, AuthUser } from '../types';
import { apiClient } from './apiClient';

export class AuthService {
  // Login user
  static async login(loginData: LoginRequest): Promise<JwtResponse> {
    const response = await apiClient.post('/auth/signin', loginData);
    const jwtResponse = response.data;
    
    // Store token and user data in localStorage (persists across sessions)
    localStorage.setItem('token', jwtResponse.token);
    localStorage.setItem('user', JSON.stringify({
      userId: jwtResponse.userId,
      phoneNumber: jwtResponse.phoneNumber,
      email: jwtResponse.email,
      firstName: jwtResponse.firstName,
      lastName: jwtResponse.lastName,
      userType: jwtResponse.userType,
      role: jwtResponse.role
    }));
    
    return jwtResponse;
  }

  // Register user
  static async signup(signupData: SignupRequest): Promise<any> {
    try {
      console.log('AuthService.signup - Request payload:', JSON.stringify(signupData, null, 2));
      const response = await apiClient.post('/auth/signup', signupData);
      return response.data;
    } catch (error: any) {
      // Log the full error for debugging
      console.error('Signup error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        fullError: error
      });
      // Also log the response data separately for easier reading
      if (error.response?.data) {
        console.error('Backend error response:', JSON.stringify(error.response.data, null, 2));
      }
      throw error;
    }
  }

  // Logout user
  static logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  // Get current user from localStorage
  static getCurrentUser(): AuthUser | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return null;
    }
    try {
      return JSON.parse(userStr) as AuthUser;
    } catch {
      console.warn('Invalid user JSON in localStorage; clearing auth.');
      this.logout();
      return null;
    }
  }

  // Get current token from localStorage
  static getToken(): string | null {
    return localStorage.getItem('token');
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  }

  // Validate token by checking if it's expired
  static isTokenValid(token: string): boolean {
    try {
      // Decode JWT token to check expiration
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      
      // Check if token is expired
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        return false; // Token is expired
      }
      return true; // Token is valid
    } catch (error) {
      console.error('Error validating token:', error);
      return false; // Invalid token format
    }
  }

  // Validate current stored token
  static validateStoredToken(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }
    
    const isValid = this.isTokenValid(token);
    if (!isValid) {
      // Clear invalid token
      this.logout();
    }
    return isValid;
  }

  // Check if phone number exists
  static async checkPhoneNumber(phoneNumber: string): Promise<boolean> {
    const response = await apiClient.get(`/auth/check-phone/${phoneNumber}`);
    return response.data.exists;
  }

  // Check if email exists
  static async checkEmail(email: string): Promise<boolean> {
    const response = await apiClient.get(`/auth/check-email/${email}`);
    return response.data.exists;
  }

  // Refresh token (if needed in future)
  static async refreshToken(): Promise<string> {
    const response = await apiClient.post('/auth/refresh');
    const newToken = response.data.token;
    localStorage.setItem('token', newToken);
    return newToken;
  }

  // Change password
  static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  // Reset password (forgot password)
  static async resetPassword(identifier: string, method: 'phone' | 'email', newPassword: string): Promise<void> {
    const response = await apiClient.post('/auth/reset-password', {
      identifier,
      method,
      newPassword,
    });
    return response.data;
  }
}

export default AuthService;
